/**
 * OneUptime OTLP contract shared by the browser and server runtimes.
 *
 * Names and paths here are not style choices: `service.name` is the key a RUM
 * application is filed under and the key source maps are stored against
 * (official docs, telemetry/source-maps), and `/otlp/v1/{signal}` is the only
 * ingest path the instance accepts.
 *
 * @module lib/observability/otlp
 */

/** Browser telemetry identity — the RUM application name. */
export const RUM_SERVICE_NAME = 'craft-website-web'
/** Server telemetry identity — a backend Service, not the RUM application. */
export const SERVER_SERVICE_NAME = 'craft-website-production'

/**
 * The one span name `captureException` creates. The sampler keys off it so an
 * error is recorded even when the trace ratio is 0 — keep the two in sync by
 * importing this constant, never by repeating the string.
 */
export const ERROR_SPAN_NAME = 'app.exception'

/** One navigation of the App Router is traced as a span with this name. */
export const ROUTE_CHANGE_SPAN_NAME = 'route-change'

export type OtlpSignal = 'traces' | 'metrics' | 'logs'

/** `https://host` + the signal path the instance exposes. */
export function otlpEndpoint(baseUrl: string, signal: OtlpSignal): string {
  return `${baseUrl.replace(/\/+$/, '')}/otlp/v1/${signal}`
}

/**
 * Resource attributes shared by every signal.
 *
 * `service.version` must equal the value the source maps were uploaded under,
 * so it comes from `identity.releaseVersion()` — the build-injected commit —
 * rather than from a hand-written constant.
 */
export function serviceResourceAttributes(
  serviceName: string,
  release: string,
  environment: string,
): Record<string, string> {
  return {
    'service.name': serviceName,
    'service.version': release,
    'deployment.environment.name': environment,
  }
}

/** The header OneUptime requires on every OTLP request. */
export function oneUptimeHeaders(token: string): Record<string, string> {
  return { 'x-oneuptime-token': token }
}
